import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { POPUP_SECTIONS } from "../../constant/constant";
import { usePopupFormContext } from "../../hooks/usePopupFormContext";
import { useProductPageUpsellSelection } from "./ProductPageUpsellSelectionContext";
import { addProductPageBundleLineItems } from "../../hooks/useProductPageBundleAddToCart";
import { showCartAddError } from "../../utils/cart-add-toast";
import { formatStorefrontMoney } from "../../utils/format-storefront-money";
import { findVariantByFormValue } from "../../layouts/product-recommendation/utils/product-variant-utils";
import { parseCssStringToObject, addPx, calculateDiscountedPrice, buildFontFamilyStyle } from "../../helper";
import { ensureProductUpsellAddToCartButtonSettings } from "../../../shared/product-upsell-recommendation-defaults.js";
import { mergeDisplaySettings } from "../../../shared/product-block-display.js";
import { CART_DRAWER_UPSELL_PRICE_DISPLAY } from "../../../shared/custom-cart-drawer-template.js";
import styles from "./product-page-fbt-claim-button.module.css";

function resolveVariantPrice(productData, formValues, productIndex) {
  const variantValue = formValues?.[`${POPUP_SECTIONS.VARIANT_PICKER}-${productIndex}`];
  const variant =
    findVariantByFormValue(productData?.variants, variantValue) ||
    productData?.variants?.[0];
  const rawPrice = Number(variant?.price ?? productData?.price ?? 0);
  const discounted = calculateDiscountedPrice(
    rawPrice,
    productData?.discount_value,
    productData?.discount_type
  );
  const price =
    Number.isFinite(Number(discounted)) && Number(discounted) > 0
      ? Number(discounted)
      : Number.isFinite(rawPrice)
        ? rawPrice
        : 0;
  const compareAt = Number(rawPrice);
  return {
    price,
    compareAt: Number.isFinite(compareAt) && compareAt > price ? compareAt : 0,
  };
}

function resolveButtonLabel(buttonSection) {
  const raw = buttonSection?.text;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed || "Claim Offer";
  }
  if (raw && typeof raw === "object") {
    const trimmed = String(raw.html || raw.text || "").trim();
    return trimmed || "Claim Offer";
  }
  return "Claim Offer";
}

function findLiveProductPagePriceSection(popupData, formValues) {
  const schema = formValues?.schema || popupData?.schema || [];
  for (const step of schema) {
    for (const section of step?.section || []) {
      if (section?.type !== "PRODUCT") continue;
      const priceSection = (section?.sections || []).find((sub) => sub?.type === POPUP_SECTIONS.PRICE);
      if (priceSection) return priceSection;
    }
  }
  return null;
}

function resolveClaimButtonStyle(buttonSection, isMobile) {
  if (!buttonSection) return {};
  const viewport = buttonSection?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"] || {};
  const override = parseCssStringToObject(
    buttonSection?.setting?.overrideCss?.[isMobile ? "mobile" : "desktop"]
  );
  const chrome = { ...viewport, ...override };
  const {
    fontFamily,
    fontSize,
    fontWeight,
    color,
    lineHeight,
    letterSpacing,
    textAlign,
    rotation,
    marginBottom,
    marginBottomPx,
    ...boxStyle
  } = chrome;

  return {
    buttonStyle: {
      ...boxStyle,
      borderRadius: addPx(boxStyle.borderRadius || viewport.borderRadius),
      width: boxStyle.width || "100%",
      display: "flex",
      opacity: 1,
      pointerEvents: "auto",
    },
    textStyle: {
      ...buildFontFamilyStyle(fontFamily),
      fontSize: addPx(fontSize),
      fontWeight,
      color,
      lineHeight,
      letterSpacing,
      textAlign,
    },
  };
}

export default function ProductPageFbtClaimButton({
  popupData,
  products = [],
  productSection,
  isPreviewMode = false,
  embeddedInEditor = false,
  isMobile = false,
  isProductSelected,
}) {
  const { watch } = usePopupFormContext();
  const formValues = watch();
  const selection = useProductPageUpsellSelection();
  const [isAdding, setIsAdding] = useState(false);
  const isEditorSurface = isPreviewMode || embeddedInEditor;

  const buttonSection = useMemo(() => {
    const schema = formValues?.schema || popupData?.schema || [];
    for (const step of schema) {
      for (const section of step?.section || []) {
        if (section?.type !== "PRODUCT") continue;
        const liveButton = (section?.sections || []).find((sub) => sub?.type === POPUP_SECTIONS.BUTTON);
        if (liveButton) {
          return ensureProductUpsellAddToCartButtonSettings({ ...liveButton });
        }
      }
    }
    const raw = (productSection?.sections || []).find((sub) => sub?.type === POPUP_SECTIONS.BUTTON);
    return raw ? ensureProductUpsellAddToCartButtonSettings({ ...raw }) : null;
  }, [formValues, popupData, productSection]);

  const priceSection = useMemo(() => {
    return (
      findLiveProductPagePriceSection(popupData, formValues) ||
      (productSection?.sections || []).find((sub) => sub?.type === POPUP_SECTIONS.PRICE)
    );
  }, [formValues, popupData, productSection]);

  const showCompareAtOnButton = useMemo(() => {
    const display = mergeDisplaySettings(
      priceSection?.setting?.display,
      CART_DRAWER_UPSELL_PRICE_DISPLAY
    );
    return display.showCompareAt !== false;
  }, [priceSection]);

  const buttonLabel = useMemo(() => resolveButtonLabel(buttonSection), [buttonSection]);

  const { buttonStyle, textStyle } = useMemo(
    () => resolveClaimButtonStyle(buttonSection, isMobile),
    [buttonSection, isMobile]
  );

  const selectedIndices = useMemo(() => {
    if (typeof isProductSelected === "function") {
      return products.map((_, index) => index).filter((index) => isProductSelected(index));
    }
    const items = selection?.getSelectedLineItems?.() || [];
    return items.map((item) => item.productIndex);
  }, [formValues, isProductSelected, products, selection]);

  // Default locked (legacy FBT). Merchant can turn off in Settings.
  const lockFirst =
    typeof selection?.lockFirstProductSelection === "boolean"
      ? selection.lockFirstProductSelection
      : productSection?.setting?.layout?.lockFirstProductSelection !== false;

  // Locked: Claim needs an add-on (index > 0). Unlocked: any selection works.
  const hasClaimableSelection = lockFirst
    ? selectedIndices.some((index) => index > 0)
    : selectedIndices.length > 0;

  const pricingIndices = useMemo(() => {
    if (!lockFirst) {
      return [...selectedIndices].sort((a, b) => a - b);
    }
    const indices = new Set(selectedIndices);
    indices.add(0);
    return [...indices].sort((a, b) => a - b);
  }, [lockFirst, selectedIndices]);

  const totals = useMemo(() => {
    let total = 0;
    let compareTotal = 0;
    pricingIndices.forEach((productIndex) => {
      const productData = products[productIndex];
      if (!productData) return;
      const { price, compareAt } = resolveVariantPrice(productData, formValues, productIndex);
      total += price;
      compareTotal += compareAt > 0 ? compareAt : price;
    });
    return { total, compareTotal };
  }, [formValues, pricingIndices, products]);

  const handleClaim = async () => {
    if (isEditorSurface || isAdding) return;
    if (!hasClaimableSelection || !selection?.getSelectedLineItems) return;
    setIsAdding(true);
    try {
      // Keep selection catalog in sync before resolving variant line items.
      selection?.setBundleProducts?.(products);
      // Do not bypass hybrid cart/add patch — same as pre-Jul-15 behavior so
      // checked FBT items are added AND the custom cart drawer can open.
      await addProductPageBundleLineItems(
        popupData,
        () => selection.getSelectedLineItems(),
        {
          products,
          bypassCartFetchPatch: false,
        }
      );
      selection?.clearProductSelection?.();
    } catch (error) {
      console.error("[ProductPageFbtClaim] Failed to add bundle:", error);
      showCartAddError(error);
    } finally {
      setIsAdding(false);
    }
  };

  const priceLabel = formatStorefrontMoney(totals.total);
  const compareLabel =
    showCompareAtOnButton && totals.compareTotal > totals.total
      ? formatStorefrontMoney(totals.compareTotal)
      : "";

  const showPrices = products.length > 0;
  const isDisabled = isAdding || (!isEditorSurface && !hasClaimableSelection);

  return (
    <button
      type="button"
      className={`weupsell_add_cart_button ${styles.claimBtn}${isDisabled ? ` ${styles.claimBtnDisabled}` : ` ${styles.claimBtnActive}`}`}
      style={isDisabled ? undefined : buttonStyle}
      disabled={isDisabled}
      onClick={handleClaim}
      data-fbt-claim-button="true"
      data-weupsell-add-cart-button="true"
    >
      <span className={styles.claimLabel} style={isDisabled ? undefined : textStyle}>
        {isAdding ? "Adding…" : buttonLabel}
      </span>
      {showPrices ? (
        <>
          <span className={styles.claimDot} style={isDisabled ? undefined : textStyle} aria-hidden>
            •
          </span>
          <span className={styles.claimPrices}>
            <span className={styles.salePrice} style={isDisabled ? undefined : textStyle}>
              {priceLabel}
            </span>
            {compareLabel ? (
              <span className={styles.comparePrice} style={isDisabled ? undefined : textStyle}>
                {compareLabel}
              </span>
            ) : null}
          </span>
        </>
      ) : null}
    </button>
  );
}

ProductPageFbtClaimButton.propTypes = {
  popupData: PropTypes.object,
  products: PropTypes.array,
  productSection: PropTypes.object,
  isPreviewMode: PropTypes.bool,
  embeddedInEditor: PropTypes.bool,
  isMobile: PropTypes.bool,
  isProductSelected: PropTypes.func,
};
